import isBound from "../../form/BoundField/isBound";
import * as React from "react";
import pluginRegistry, {ActionEditProps} from "../pluginRegistry";
import {BaseComponent} from "components/BaseComponent/BaseComponent";
import SpecialVariables from "client/specialVariables";
import {repository} from "clientInstance";
import Roles from "../Roles";
import {ActionSummaryProps} from "../actionSummaryProps";
import {ActionExecutionLocation} from "../../../client/resources/actionExecutionLocation";
import {
    ExpandableFormSection,
    Summary,
    FormSectionHeading,
    Text,
    Note,
    UnstructuredFormSection
} from "components/form";
import {
    AccountType,
    AccountResource,
    AzureCloudServiceEndpointDeploymentSlot,
    Permission,
    InitialisePrimaryPackageReference, GetPrimaryPackageReference, SetPrimaryPackageReference
} from "client/resources";
import FeedResource, {BuiltInFeedId} from "client/resources/feedResource";
import CommonSummaryHelper from "utils/CommonSummaryHelper/CommonSummaryHelper";
import PackageSelector from "components/PackageSelector/PackageSelector";
import ExternalLink from "components/Navigation/ExternalLink/ExternalLink";
import { BoundAccountSelect } from "../../form/AccountSelect/AccountSelect";
import RadioButton from "components/form/RadioButton/RadioButton";
import RadioButtonGroup from "components/form/RadioButton/RadioButtonGroup";
import AzureServerTargetRolesInfo from "components/Actions/azurePowerShell/AzureServerTargetRolesInfo";
import AzurePowerShellScriptInfo from "components/Actions/azurePowerShell/AzurePowerShellScriptInfo";
import AzureCloudServiceSelector from "./AzureCloudServiceSelector";
import WarningFormSection from "components/form/Sections/WarningFormSection";
import {getFeedName} from "../getFeedName";
import { TargetRoles } from "areas/projects/components/DeploymentProcess/ActionDetails";
import { BoundSelect } from "components/form/Select/Select";
import { Callout, CalloutType } from "components/Callout/Callout";
import PermissionCheck, {isAllowed } from "../../PermissionCheck/PermissionCheck";

interface AzureCloudServiceActionSummaryState {
    feedName: string;
}

class AzureCloudServiceActionSummary extends BaseComponent<ActionSummaryProps, AzureCloudServiceActionSummaryState> {
    constructor(props: any) {
        super(props);
        this.state = {feedName: null };
    }

    async componentDidMount() {
        const pkg = GetPrimaryPackageReference(this.props.packages);
        this.setState({feedName: await getFeedName(pkg.FeedId)});
    }

    render() {
        const pkg = GetPrimaryPackageReference(this.props.packages);
        return <div>
            Deploy Azure Cloud Service
            <strong> {pkg.PackageId} </strong>
            from {this.state.feedName ? <strong>{this.state.feedName}</strong> : <em>{pkg.FeedId}</em>}
            {this.props.targetRolesAsCSV && <span> on behalf of targets in <Roles rolesAsCSV={this.props.targetRolesAsCSV} /> </span>}
        </div>;
    }
}

interface AzureCloudServiceProperties {
    "Octopus.Action.Azure.SubscriptionId": string;
    "Octopus.Action.Azure.AccountId": string;
    "Octopus.Action.Azure.CloudServiceName": string;
    "Octopus.Action.Azure.StorageAccountName": string;
    "Octopus.Action.Azure.Slot": string;
    "Octopus.Action.Azure.SwapIfPossible": string;
    "Octopus.Action.Azure.UseCurrentInstanceCount": string;
}

interface AzureCloudServiceActionEditState {
    feeds: FeedResource[];
    accounts: AccountResource[];
    accountIsBound: boolean;
}

class AzureCloudServiceActionEdit extends BaseComponent<ActionEditProps<AzureCloudServiceProperties>, AzureCloudServiceActionEditState> {
    constructor(props: any) {
        super(props);
        this.state = {
            feeds: [],
            accounts: [],
            accountIsBound: isBound(props.properties["Octopus.Action.Azure.AccountId"], false)
        };
    }

    async componentDidMount() {
        await this.loadFeeds();
        this.props.doBusyTask(async () => {
            if (!!(this.props.properties["Octopus.Action.Azure.AccountId"]) && isAllowed({permission: Permission.AccountView, wildcard: true})) {
                this.setState({
                    accounts: await repository.Accounts.all()
                });
            }

            const properties: any = {};
            const isLegacyActionType = !!(properties["Octopus.Action.Azure.AccountId"]);
            if (isLegacyActionType) {
                if (!this.props.properties["Octopus.Action.Azure.Slot"]) {
                    properties["Octopus.Action.Azure.Slot"] = AzureCloudServiceEndpointDeploymentSlot.Staging;
                }
                if (!this.props.properties["Octopus.Action.Azure.SwapIfPossible"]) {
                    properties["Octopus.Action.Azure.SwapIfPossible"] = true;
                }
                if (!this.props.properties["Octopus.Action.Azure.UseCurrentInstanceCount"]) {
                    properties["Octopus.Action.Azure.UseCurrentInstanceCount"] = true;
                }
            }
            this.props.setProperties(properties, true);
        });

        this.props.setPackages(InitialisePrimaryPackageReference(this.props.packages), true);
    }

    accountSummary() {
        const summary = [];
        const accountId = this.props.properties["Octopus.Action.Azure.AccountId"];
        if (accountId) {
            const account = this.state.accounts.find(x => x.Id === accountId);
            if (account) {
                summary.push(<span>Using the <strong>{account.Name}</strong> account</span>);

                (this.props.properties["Octopus.Action.Azure.CloudServiceName"])
                    ? summary.push(<span>, with the <strong>{this.props.properties["Octopus.Action.Azure.CloudServiceName"]}</strong> cloud service</span>)
                    : summary.push(<span>, with <em>(no cloud service selected)</em></span>);

                (this.props.properties["Octopus.Action.Azure.StorageAccountName"])
                    ? summary.push(<span>, and the <strong>{this.props.properties["Octopus.Action.Azure.StorageAccountName"]}</strong> storage account</span>)
                    : summary.push(<span>, and <em>(no storage account selected)</em></span>);

                return Summary.summary(React.Children.toArray(summary));
            }
            if (isBound(accountId, false)) {
                return Summary.summary(<span>Account is bound to <strong>{accountId}</strong></span>);
            }
        }
        return Summary.placeholder("No account has been selected");
    }

    slotSummary() {
        const slot = this.props.properties["Octopus.Action.Azure.Slot"];
        if (slot) {
            if (slot === AzureCloudServiceEndpointDeploymentSlot.Staging) {
                return Summary.default(<span>Using the {slot} deployment slot</span>);
            }
            return Summary.summary(<span>Using the {slot} deployment slot</span>);
        }
        return Summary.placeholder("Deployment slot not specified");
    }

    swapSummary() {
        const swap = this.props.properties["Octopus.Action.Azure.SwapIfPossible"];
        if (swap === "True") {
            return Summary.default("When deploying to production swap staging to production, if possible");
        }
        if (swap === "False") {
            return Summary.summary("When deploying to production always perform a new deployment");
        }
        return Summary.placeholder("Swap setting not specified");
    }

    instanceCountSummary() {
        const instanceCount = this.props.properties["Octopus.Action.Azure.UseCurrentInstanceCount"];
        if (instanceCount === "True") {
            return Summary.default("Using the instance count from the current Azure deployment");
        }
        if (instanceCount === "False") {
            return Summary.summary("Using the instance count defined in the service configuration XML file");
        }
        return Summary.placeholder("Instance count not specified");
    }

    getAccountId = () => {
        return isBound(this.props.properties["Octopus.Action.Azure.AccountId"]) ? undefined : this.props.properties["Octopus.Action.Azure.AccountId"];
    }

    render() {
        // The package is initialized in componentDidMount, but render gets called before the update is propagated
        if (!this.props.packages || this.props.packages.length === 0) {
            return null;
        }

        const pkg = GetPrimaryPackageReference(this.props.packages);

        const properties = this.props.properties;
        const isLegacyActionType = !!(properties["Octopus.Action.Azure.AccountId"]);
        const help = this.state.feeds.length > 0
        ? <span>
            This step is used to deploy the contents of a package.
            The package that you wish to deploy should contain all the files needed to run your application. Learn
            more about <ExternalLink href="DocumentationPackaging">what your packages should contain, and how to create them</ExternalLink>.
        </span>
        : <span>Choose the package you which to deploy</span>;

        return <div>
            <WarningFormSection>
                <Callout type={CalloutType.Warning} title="Cloud Services are considered legacy">
                    Azure have announced that Cloud Services are now a legacy service and <i>Service Fabric</i> is recommended for new development.
                    Please consider <ExternalLink href="WindowsAzure">alternate applications</ExternalLink> that you can deploy to Azure.
                </Callout>
            </WarningFormSection>

            <WarningFormSection>
                {this.props.additionalActions && <AzureServerTargetRolesInfo stepTargetRoles={this.props.additionalActions.stepTargetRoles} isCompatibleWithCloudRegions={false} />}
                <AzurePowerShellScriptInfo actionType={this.props.plugin.actionType}/>
            </WarningFormSection>

            <FormSectionHeading title="Package"  />
            <ExpandableFormSection
                errorKey="Octopus.Action.Package.PackageId|Octopus.Action.Package.FeedId"
                isExpandedByDefault={this.props.expandedByDefault}
                title="Package"
                summary={CommonSummaryHelper.packageSummary(pkg, this.state.feeds)}
                help={help}>
                <PackageSelector
                    packageId={pkg.PackageId}
                    feedId={pkg.FeedId}
                    onPackageIdChange={packageId => this.props.setPackages(SetPrimaryPackageReference({PackageId: packageId}, this.props.packages))}
                    onFeedIdChange={feedId => this.props.setPackages(SetPrimaryPackageReference({FeedId: feedId}, this.props.packages))}
                    packageIdError={this.props.getFieldError("Octopus.Action.Package.PackageId")}
                    feedIdError={this.props.getFieldError("Octopus.Action.Package.FeedId")}
                    projectId={this.props.projectId}
                    feeds={this.state.feeds}
                    localNames={this.props.localNames}
                    refreshFeeds={this.loadFeeds} />
            </ExpandableFormSection>

            {isLegacyActionType &&
                <div>
                    <FormSectionHeading title="Azure"  />
                    <PermissionCheck
                         permission={Permission.AccountView}
                        wildcard={true}
                        alternate={
                             <Callout type={CalloutType.Information}>
                                The {Permission.AccountView} permission is required to change the Azure settings.
                            </Callout>}>
                        <ExpandableFormSection
                            errorKey="Octopus.Action.Azure.AccountId"
                            isExpandedByDefault={this.props.expandedByDefault}
                            title="Account"
                            summary={this.accountSummary()}
                            help={"Select the account to use for the connection. Only Azure management-certificate accounts may be selected."}>
                            {(this.props.properties["Octopus.Action.Azure.SubscriptionId"] && !this.props.properties["Octopus.Action.Azure.AccountId"]) &&
                            <UnstructuredFormSection>
                                <Note>This step is using a subscription ID value imported from Octopus Deploy 2.
                                    This value, along with the default Azure certificate, will be used until an Account is selected below.
                                    From then on the subscription details from the selected Account will be used.</Note>
                                <Text
                                    value={this.props.properties["Octopus.Action.Azure.SubscriptionId"]}
                                    label="Subscription"
                                    disabled={true}
                                    onChange={null}/>
                            </UnstructuredFormSection>}
                            <BoundAccountSelect
                                variableLookup={{
                                    localNames: this.props.localNames,
                                    projectId: this.props.projectId
                                }}
                                resetValue={properties["Octopus.Action.Azure.AccountId"]}
                                label="Account"
                                value={properties["Octopus.Action.Azure.AccountId"]}
                                isBound={this.state.accountIsBound}
                                onIsBoundChanged={(value: boolean) => this.setState({accountIsBound: value})}
                                type={[AccountType.AzureSubscription]}
                                allowClear={true}
                                onChange={(x) => this.props.setProperties({["Octopus.Action.Azure.AccountId"]: x})}
                                error={this.props.getFieldError("Octopus.Action.Azure.AccountId")}
                                items={this.state.accounts}
                                onRequestRefresh={this.refreshAccounts}
                            />
                            <AzureCloudServiceSelector
                                doBusyTask={this.props.doBusyTask}
                                properties={this.props.properties}
                                setProperties={this.props.setProperties}
                                getFieldError={this.props.getFieldError}
                                projectId={this.props.projectId}
                                accountId={this.getAccountId()}
                                isAccountBound={this.state.accountIsBound}
                                localNames={this.props.localNames}
                            />
                        </ExpandableFormSection>

                        <FormSectionHeading title="Deployment"  />
                        <ExpandableFormSection
                            errorKey="Octopus.Action.Azure.Slot"
                            isExpandedByDefault={this.props.expandedByDefault}
                            title="Slot"
                            summary={this.slotSummary()}
                            help="Select a slot to deploy to.">
                            <Note>Deployment Slots provide a nice way to implement Blue-Green deployments. Learn more about <ExternalLink href="VipSwap">Deployment Slots</ExternalLink>
                            </Note>
                            <BoundSelect
                                variableLookup={{
                                    localNames: this.props.localNames,
                                    projectId: this.props.projectId
                                }}
                                resetValue={""}
                                label="Slot"
                                value={this.props.properties["Octopus.Action.Azure.Slot"]}
                                items={["Staging", "Production"].map(item => ({value: item, text: item}))}
                                onChange={(val) => this.props.setProperties({["Octopus.Action.Azure.Slot"]: val})}
                                error={this.props.getFieldError("Octopus.Action.Azure.Slot")} />
                        </ExpandableFormSection>

                        <ExpandableFormSection
                            errorKey="Octopus.Action.Azure.SwapIfPossible"
                            isExpandedByDefault={this.props.expandedByDefault}
                            title="Swap"
                            summary={this.swapSummary()}
                            help="Specify to swap staging to production rather than a new deployment.">
                            <RadioButtonGroup
                                value={this.props.properties["Octopus.Action.Azure.SwapIfPossible"]}
                                onChange={(val: string) => this.props.setProperties({["Octopus.Action.Azure.SwapIfPossible"]: val})}
                                error={this.props.getFieldError("Octopus.Action.Azure.SwapIfPossible")}>
                                <RadioButton value={"True"} label="Swap staging to production if possible"/>
                                <Note>Azure can swap staging to production deployments by switching virtual IP addresses.</Note>
                                <RadioButton value={"False"} label="Always deploy"/>
                            </RadioButtonGroup>
                        </ExpandableFormSection>

                        <ExpandableFormSection
                            errorKey="Octopus.Action.Azure.UseCurrentInstanceCount"
                            isExpandedByDefault={this.props.expandedByDefault}
                            title="Instance Count"
                            summary={this.instanceCountSummary()}
                            help="Select the source to use the instance count from.">
                            <RadioButtonGroup
                                value={this.props.properties["Octopus.Action.Azure.UseCurrentInstanceCount"]}
                                onChange={(val: string) => this.props.setProperties({["Octopus.Action.Azure.UseCurrentInstanceCount"]: val})}
                                error={this.props.getFieldError("Octopus.Action.Azure.UseCurrentInstanceCount")}>
                                <RadioButton value={"True"}
                                            label="Use the instance count from the current Azure deployment"/>
                                <RadioButton value={"False"}
                                            label="Use the instance count defined in the service configuration XML file"/>
                            </RadioButtonGroup>
                            <Callout type={CalloutType.Information}>If you have previously scaled your service by changing the number of instances using the Azure management portal, Octopus can retrieve these values
                            and use them in the deployment, so that the instance count remains unchanged.</Callout>
                        </ExpandableFormSection>
                    </PermissionCheck>
                </div>}
        </div>;
    }

    private refreshAccounts = () => {
        return this.props.doBusyTask(async () => {
            this.setState({ accounts: await repository.Accounts.all() });
        });
    }

    private loadFeeds = () => {
        return this.props.doBusyTask(async () => {
            this.setState({ feeds: await repository.Feeds.all() });
        });
    }
}

pluginRegistry.registerDeploymentAction({
    executionLocation: ActionExecutionLocation.AlwaysOnServer,
    actionType: "Octopus.AzureCloudService",
    summary: (properties, targetRolesAsCSV, packages) => <AzureCloudServiceActionSummary properties={properties} packages={packages} targetRolesAsCSV={targetRolesAsCSV} />,
    edit: AzureCloudServiceActionEdit,
    canHaveChildren: (step) => false,
    canBeChild: true,
    targetRoleOption: (action) => {
        // Azure steps (pre Q1.2018) allowed you to select accounts directly on the step, making target selection optional.
        const isLegacyActionType = !!(action ? action.Properties["Octopus.Action.Azure.AccountId"] : null);
        return isLegacyActionType ? TargetRoles.Optional : TargetRoles.Required;
    },
    hasPackages: (action) => true,
    features: {
        optional: ["Octopus.Features.ConfigurationTransforms", "Octopus.Features.ConfigurationVariables", "Octopus.Features.CustomDirectory",
        "Octopus.Features.CustomScripts", "Octopus.Features.JsonConfigurationVariables", "Octopus.Features.SubstituteInFiles"]
    }
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/Actions/azureCloudService/azureCloudServiceAction.tsx